import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { type StaticDecode, Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import { LocString, Nullable, ReadonlyNullable } from 'schema/schema';
import { Image, MongoAndPgEntity } from 'schema/shared';
import { useLogUpdatedCVs } from 'stores/ActivityLogs';
import { httpChecked, queryClient } from 'util/fetch';

/**
 * The version of User returned from the API calls to examine usage of project fields on CVs
 */
export const UsageUser = Type.Interface([MongoAndPgEntity], {
  name: Type.Readonly(Type.String()),
  cv_id: Type.Readonly(Type.String()),
  can_write: Type.Readonly(Type.Boolean()),
  image: Type.Readonly(Image),
  project_experience_ids: Type.Readonly(Type.Array(Type.String())),
});
export type UsageUser = StaticDecode<typeof UsageUser>;
const UsageUsers = Type.Array(UsageUser);

const UsersByVariant = Type.Record(Type.String(), UsageUsers);

export const FieldUsage = Type.Object({
  official_value: ReadonlyNullable(Type.String()),
  users_with_official_value: Type.Readonly(UsageUsers),
  users_with_variants: Type.Readonly(UsageUsers),
  users_with_variants_ro: Type.Readonly(UsageUsers),
  variants: Type.Readonly(Type.Array(Nullable(Type.String()))),
  users_by_variant: Type.Readonly(UsersByVariant),
  users_by_variant_ro: Type.Readonly(UsersByVariant),
});
export type FieldUsage = StaticDecode<typeof FieldUsage>;

export const CustomerContactUsage = Type.Object({
  name: Type.Readonly(Type.String()),
  label: Type.Readonly(LocString),
  local_language_label: Type.Readonly(Type.String()),
  official: Type.Readonly(Type.Boolean()),
  users: Type.Readonly(UsageUsers),
});
export type CustomerContactUsage = StaticDecode<typeof CustomerContactUsage>;

const OverviewFields = Type.Object({
  customer_contacts: Type.Readonly(Type.Array(CustomerContactUsage)),
  invisible_cvs: Type.Readonly(Type.Integer({ minimum: 0 })),
});

const FieldUsages = Type.Object({
  dates: Type.Readonly(FieldUsage),
  industry: Type.Readonly(FieldUsage),
  project_extent_hours: Type.Readonly(FieldUsage),
  total_extent_hours: Type.Readonly(FieldUsage),
  project_extent: Type.Readonly(FieldUsage),
  total_extent: Type.Readonly(FieldUsage),
  area: Type.Readonly(FieldUsage),
  type: Type.Readonly(FieldUsage),
  location_country_code: Type.Readonly(FieldUsage),
  project_address: Type.Readonly(FieldUsage),
  contract_type: Type.Readonly(FieldUsage),
});
type FieldUsages = StaticDecode<typeof FieldUsages>;

export type FieldName = keyof FieldUsages;

const OverviewSchema = Type.Intersect([OverviewFields, FieldUsages]);

const checker = TypeCompiler.Compile(OverviewSchema);

const queries = {
  overview: ['project_field_overview'] as const,
  project: (customer_id: string, project_id: string) =>
    [...queries.overview, { customer_id, project_id }] as const,
  fetch: (customer_id: string, project_id: string, language_code: string) =>
    queryOptions({
      queryKey: [
        ...queries.project(customer_id, project_id),
        { language_code },
      ] as const,
      queryFn: ({ signal }) => {
        const url = new URL(
          '/api/v2/company/cv/customers/project_experiences/overview',
          window.location.origin,
        );

        url.searchParams.set('language_code', language_code);
        url.searchParams.set('customer_id', customer_id);
        url.searchParams.set('project_id', project_id);

        return httpChecked.get(url, checker, { signal });
      },
    }),
};

export function useProjectFieldOverview(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  const { data } = useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    staleTime: 10_000,
  });

  return data;
}

export function useProjectFieldCustomerContacts(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  const { data } = useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    select: (data) => data.customer_contacts,
    staleTime: 10_000,
  });

  return data;
}

const synchroniseFieldSchema = Type.Object({
  changed: Type.Readonly(Type.Integer()),
  unchanged: Type.Readonly(Type.Integer()),
  cannot_write: Type.Readonly(Type.Integer()),
  updated_cvs: Type.Readonly(
    Type.Array(
      Type.Object({
        user_id: Type.Readonly(Type.String()),
        cv_id: Type.Readonly(Type.String()),
        name: ReadonlyNullable(Type.String()),
      }),
    ),
  ),
});

const synchroniseFieldChecker = TypeCompiler.Compile(synchroniseFieldSchema);

export function useSynchroniseProjectFieldOverview(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  const logUpdatedCVs = useLogUpdatedCVs();

  return useMutation({
    mutationFn: ({ field_group }: { field_group: FieldName | 'all' }) => {
      const body = {
        customer_id,
        project_id,
        country_code: language_code,
        field_group,
      };

      return httpChecked.put(
        '/api/v2/company/cv/customers/project_experiences/synchronise',
        synchroniseFieldChecker,
        { body },
      );
    },
    onSuccess: (data) => {
      logUpdatedCVs(data.updated_cvs);

      return invalidateProjectFieldsOverview(customer_id, project_id);
    },
  });
}

export async function invalidateProjectFieldsOverview(
  customer_id: string,
  project_id: string,
) {
  await queryClient.invalidateQueries({
    queryKey: queries.project(customer_id, project_id),
  });
}

export async function invalidateAllProjectFieldsOverviews() {
  await queryClient.invalidateQueries({ queryKey: queries.overview });
}

export async function prefetchProjectFieldOverview(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  await queryClient.prefetchQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    staleTime: Infinity,
  });
}
